import { Menu, RefreshCw, Compass, Settings, LogOut, FileDown, Shield, Sun, Moon, Check, Users, Edit } from 'lucide-react';
import NotificationPanel from './NotificationPanel';
import { useAuthStore, useFeedsStore } from '../store/useStore';
import { feeds as feedsApi } from '../lib/api';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '../hooks/useTheme';

interface MobileHeaderProps {
  onMenuClick: () => void;
}

export default function MobileHeader({ onMenuClick }: MobileHeaderProps) {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const user = useAuthStore((state) => state.user);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const token = useAuthStore((state) => state.token)!;
  const feeds = useFeedsStore((state) => state.feeds);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isUserMenuOpen]);

  function go(path: string) {
    navigate(path);
    setIsUserMenuOpen(false);
  }

  function handleLogout() {
    clearAuth();
    navigate('/login');
    setIsUserMenuOpen(false);
  }

  async function handleRefreshAll() {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      let totalArticlesAdded = 0;

      for (const subscription of feeds) {
        try {
          const result = await feedsApi.refresh(token, subscription.feed.id);
          if (result.articlesAdded > 0) {
            totalArticlesAdded += result.articlesAdded;
          }
        } catch (error) {
          console.error(`Failed to refresh feed ${subscription.feed.title}:`, error);
        }
      }

      if (totalArticlesAdded > 0) {
        toast.success(`Found ${totalArticlesAdded} new article${totalArticlesAdded !== 1 ? 's' : ''}`);
        const feedsData = await feedsApi.getAll(token);
        useFeedsStore.getState().setFeeds(feedsData);
        window.dispatchEvent(new CustomEvent('feeds-refreshed'));
      } else {
        toast.success('All feeds are up to date');
      }

      setRefreshSuccess(true);
      setTimeout(() => setRefreshSuccess(false), 1500);
    } catch {
      toast.error('Failed to refresh feeds');
    } finally {
      setIsRefreshing(false);
    }
  }

  const menuItem = 'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-sunken border-b border-edge-subtle last:border-b-0';

  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 bg-canvas border-b border-edge z-40 safe-area-top">
      <div className="flex items-center h-12">
        <button
          onClick={onMenuClick}
          className="h-full px-3 border-r border-edge text-ink-secondary"
          aria-label="Open feeds"
        >
          <Menu className="w-5 h-5" strokeWidth={1.75} />
        </button>

        <button
          onClick={() => navigate('/read')}
          className="wordmark text-lg px-3"
        >
          ou<em>r</em>ss
        </button>

        <div className="flex-1" />

        <div className="flex items-center h-full">
          <button
            onClick={handleRefreshAll}
            disabled={isRefreshing}
            className={`h-full px-3 border-l border-edge ${
              refreshSuccess ? 'text-pine' : isRefreshing ? 'text-ink-faint' : 'text-ink-secondary'
            }`}
            title={isRefreshing ? 'Refreshing feeds...' : 'Refresh all feeds'}
          >
            {refreshSuccess ? (
              <Check className="w-5 h-5 check-animate" strokeWidth={1.75} />
            ) : (
              <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'refresh-spinning' : ''}`} strokeWidth={1.75} />
            )}
          </button>
          <div className="h-full border-l border-edge flex items-center px-1">
            <NotificationPanel />
          </div>

          <div className="relative h-full border-l border-edge" ref={menuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="h-full px-3 flex items-center"
              aria-label="Account menu"
            >
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.username}
                  className="w-7 h-7 rounded-full object-cover avatar-duotone"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-ink text-canvas flex items-center justify-center font-mono text-xs">
                  {user?.username?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-px w-56 bg-canvas border border-edge z-50 animate-fade-in">
                <div className="px-4 py-2.5 border-b border-edge">
                  <div className="kicker">Signed in as</div>
                  <div className="text-sm font-semibold text-ink">{user?.displayName || user?.username}</div>
                </div>
                <button onClick={toggleTheme} className={menuItem}>
                  {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  {isDark ? 'Light mode' : 'Dark mode'}
                </button>
                <button onClick={() => go('/users')} className={menuItem}>
                  <Users className="w-4 h-4" />
                  Users
                </button>
                <button onClick={() => go('/discover')} className={menuItem}>
                  <Compass className="w-4 h-4" />
                  Discover feeds
                </button>
                <button onClick={() => go('/collaborate')} className={menuItem}>
                  <Edit className="w-4 h-4" />
                  Collaborative posts
                </button>
                <button onClick={() => go('/settings')} className={menuItem}>
                  <Settings className="w-4 h-4" />
                  Settings
                </button>
                <button onClick={() => go('/export')} className={menuItem}>
                  <FileDown className="w-4 h-4" />
                  Export
                </button>
                {user?.isAdmin && (
                  <button onClick={() => go('/admin')} className={menuItem}>
                    <Shield className="w-4 h-4" />
                    Admin
                  </button>
                )}
                <button onClick={handleLogout} className={`${menuItem} text-signal`}>
                  <LogOut className="w-4 h-4" />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
